import { Link } from 'react-router-dom';
import { ROUTE_NAMES } from '@tsgecart/config';
import { cn } from '@tsgecart/ui';
import { useProducts } from '../../catalog/hooks/use-catalog.js';
import { StorefrontSkeletonGrid, StorefrontErrorState } from '../../components/states.js';
import type { ProductListParams } from '../../catalog/types/catalog.js';
import { ProductCard } from '../../catalog/components/product-card.js';

export interface ProductSectionProps {
  title: string;
  params: ProductListParams;
  /** Alternates the section background for visual rhythm between rails -- purely presentational. */
  tone?: 'default' | 'muted';
}

const HEADING_CLASS = 'text-xl font-bold tracking-tight text-gray-900 sm:text-2xl';
const VIEW_ALL_CLASS =
  'bg-brand text-brand-foreground hover:bg-brand/90 focus-visible:ring-brand shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 sm:text-sm';

/**
 * One homepage product rail (featured / best sellers / new arrivals), all
 * backed by real `useProducts` data (never fabricated). Fails
 * independently of every other homepage section: a loading/error/empty
 * state here never blanks the rest of the page, and an empty result
 * quietly omits the whole section rather than showing an empty shell.
 */
export function ProductSection({ title, params, tone = 'default' }: ProductSectionProps) {
  const { data, isLoading, isError, refetch } = useProducts(params);
  const sectionClass = cn('py-10 sm:py-12', tone === 'muted' && 'bg-gray-50/70');

  if (isLoading) {
    return (
      <section className={sectionClass}>
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <h2 className={cn('mb-5', HEADING_CLASS)}>{title}</h2>
          <StorefrontSkeletonGrid count={4} className="h-56" />
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <section className={sectionClass}>
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <h2 className={cn('mb-5', HEADING_CLASS)}>{title}</h2>
          <StorefrontErrorState message={`Couldn't load ${title.toLowerCase()}.`} onRetry={() => void refetch()} />
        </div>
      </section>
    );
  }

  if (!data || data.length === 0) return null;

  return (
    <section className={sectionClass}>
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="mb-5 flex items-center justify-between gap-4">
          <h2 className={HEADING_CLASS}>{title}</h2>
          <Link to={ROUTE_NAMES.shop} className={VIEW_ALL_CLASS}>
            View all
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {data.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </section>
  );
}
